<template>
    <div class="particulars-of-director-create-or-view">
        <b-sidebar
            id="sidebar-task-handler"
            data-test="sidebar"
            sidebar-class="sidebar-lg"
            :visible="isGenerateDocumentSidebarActive"
            bg-variant="white"
            shadow
            backdrop
            no-header
            right
            @change="
                (val) => $emit('update:is-generate-document-sidebar-active', val)
            "
            @hidden="clearFormData"
        >
            <template #default="{ hide }">
                <!-- Header -->
                <div
                    class="
                        d-flex
                        justify-content-between
                        align-items-center
                        content-sidebar-header
                        px-2
                        py-1
                        side-bar-header
                    "
                >
                    <h3 class="mb-0">
                        {{ sideBarTitle }}
                    </h3>

                    <div>
                        <feather-icon
                            class="ml-1 cursor-pointer"
                            icon="XIcon"
                            size="16"
                            @click="hide"
                        />
                    </div>
                </div>

                <!-- Body -->
                <validation-observer
                    v-slot="{ handleSubmit }"
                    ref="refFormObserver"
                >
                    <!-- Form -->
                    <b-form
                        class="p-2"
                        @submit.prevent="handleSubmit(onSubmit)"
                        @reset.prevent="resetForm"
                    >
                        <validation-provider
                            v-slot="{ errors }"
                            name="director"
                            rules="required"
                            class="end-align-indicator-long-select"
                        >

                            <b-form-group label="Director" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="director"
                                    ref="director"
                                    v-model="particularsOfDirectorLocal.director"
                                    class="custom-select-icon text-uppercase"
                                    :clearable="false"
                                    placeholder="Select Director"
                                    label="name"
                                    :state="errors.length > 0 ? false : null"
                                    :options="directorOptions"
                                    :disabled="isDisabled"
                                    @input="handleOnDirectorChange"
                                >
                                    <template #option="data">
                                        <div class="text-wrap text-uppercase"> {{ data.name }} </div> 
                                    </template>

                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="isFetching" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <div v-if="dataStatus !== 'view'">
                            <validation-provider
                                v-slot="{ errors }"
                                name="ID"
                                rules="required"
                            >
                                <b-form-group >
                                    <slot name="label">
                                        <label id="id-row" for="id">
                                            ID
                                            <vector
                                                data-type="icon"
                                                data-width="15px"
                                                data-height="15px"
                                                data-class="mr-.5"
                                                data-name="information"
                                            ></vector>
                                        </label>
                                    </slot>

                                    <b-popover target="id-row" triggers="hover" placement="right" variant="black">
                                        Enter your NRIC or FIN if you are a Singapore resident.
                                        <br>
                                        Enter your passport number if you are a foreigner.
                                    </b-popover>

                                    <b-form-input
                                        id='id'
                                        v-model="selectedDirectorData.nric"
                                        placeholder="Enter NRIC or passport"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        data-test="director-id"
                                        @input="onUpdateDirectorParticulars(selectedDirectorData.nric, 'Passport Number')"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <!-- id type not including the particulars update part but have to ask when password change -->
                            <validation-provider
                                v-slot="{ errors }"
                                name="ID type"
                                rules="required"
                            >
                                <b-form-group
                                    label="ID type"
                                    :state="errors.length > 0 ? false : null"
                                >
                                    <v-select
                                        ref="idType"
                                        v-model="particularsOfDirectorLocal.idType"
                                        class="custom-select-icon"
                                        placeholder="Select ID type"
                                        label="name"
                                        :clearable="false"
                                        :disabled = "isDisabled"
                                        :options="idTypeOptions"
                                        @input="handleOnChangeIdType"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name="contact number"
                                rules="required"
                            >
                                <b-form-group>
                                    <slot name="label">
                                        <label id="contactNumber" for="id">
                                            Contact number
                                            <vector
                                                data-type="icon"
                                                data-width="15px"
                                                data-height="15px"
                                                data-class="mr-.5"
                                                data-name="information"
                                            ></vector>
                                        </label>
                                    </slot>

                                    <b-form-input
                                        v-model="selectedDirectorData.contactNumber"
                                        placeholder="Enter contact number"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        data-test="contact-number"
                                        type="tel"
                                        @input="onUpdateDirectorParticulars(selectedDirectorData.contactNumber, 'Contact Number')"
                                    />

                                    <b-popover target="contactNumber" triggers="hover" placement="right" variant="black">
                                        Enter your Singapore mobile number if you are a Singapore resident. 
                                        <br>
                                        Enter your overseas mobile number if you are a foreigner.
                                    </b-popover>

                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider 
                                v-slot="{ errors }"
                                name="email"
                                rules="required|email"
                            >
                                <b-form-group label="Email">
                                    <b-form-input
                                        id="director-email"
                                        v-model="selectedDirectorData.email"
                                        placeholder="Enter email"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        data-test="director-email"
                                        @input="onUpdateDirectorParticulars(selectedDirectorData.email, 'Email Address')"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider 
                                v-slot="{ errors }"
                                name="name"
                                rules="required"
                            >
                                <b-form-group label="Name">
                                    <b-form-input
                                        id="director-name"
                                        v-model="selectedDirectorData.name"
                                        placeholder="Enter name"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        data-test="director-name"
                                        @input="onUpdateDirectorParticulars(selectedDirectorData.name, 'Name')"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name="address"
                                rules="required"
                            >
                                <b-form-group label="Address" :state="errors.length > 0 ? false : null">
                                    <b-form-textarea
                                        id="textarea"
                                        v-model="selectedDirectorData.address"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        placeholder="Enter address"
                                        data-test="address"
                                        rows="3"
                                        max-rows="3"
                                        @input="onUpdateDirectorParticulars(selectedDirectorData.address, 'Residential Address')"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name="alternative address"
                            >
                                <b-form-group label="Alternative address" :state="errors.length > 0 ? false : null">
                                    <b-form-textarea
                                        id="textarea"
                                        v-model="selectedDirectorData.alternativeAddress"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        placeholder="Enter alternative address"
                                        data-test="alternative-address"
                                        rows="3"
                                        max-rows="3"
                                        @input="onUpdateDirectorParticulars(selectedDirectorData.alternativeAddress, 'Alternative Address')"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </div>

                        <div v-if="showUpdatedParticularListTable" class="updated-particular-list mt-2">
                            <h3 class="updated-list"> Updated List </h3>
                            <table class="table table-bordered align-top">
                                <tr v-for="particular in updatedDirectorParticularList" :key="particular._id">
                                    <td style="padding: 10px 10px 10px 10px"> 
                                        {{ particular.label }}
                                    </td>
                                    <td style="padding: 10px 4px 10px 4px"> 
                                        {{ particular.changeTo }}
                                    </td>
                                    <td 
                                        v-if="dataStatus !== 'view'"
                                        class="px-.5" 
                                        style="padding: 10px 4px 10px 4px; 
                                        text-align:center; cursor: pointer;"
                                        @click="remove(particular)"
                                    > 
                                        <feather-icon icon="XCircleIcon"/>
                                    </td>
                                </tr>
                            </table>
                        </div>
                        
                        <div class="d-flex">
                            <b-button
                                v-if="! isDisabled"
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                :disabled="dataSidebarActionLoading"
                                variant="primary"
                                class="
                                    mr-1
                                    mt-1
                                    d-flex
                                    justify-content-center
                                    align-items-center
                                "
                                type="submit"
                                data-test="create-change-ro-form-btn"
                                >
                                <img
                                    v-if="dataStatus === 'prepare-for-generate' && !dataSidebarActionLoading"
                                    :src="'/icons/papers_white.svg'"
                                    class="mr-.5"
                                    alt="add-logo"
                                    width="13px"
                                />
                                <normal-loader v-if="dataSidebarActionLoading" class="action-button" />

                                <span :class="dataSidebarActionLoading ? 'mr-1' : ''">
                                    Generate
                                </span>
                            </b-button>

                            <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                class="
                                mt-1 
                                d-flex
                                justify-content-center
                                align-items-center"
                                variant="outline-primary"
                                data-test="cancel"
                                @click="$emit('update:is-generate-document-sidebar-active', false)"
                                >Cancel
                                <vector 
                                    data-type="icons" 
                                    data-width="15px" 
                                    data-height="15px" 
                                    data-name="cancel" 
                                    class="float-right ml-1"
                                    alt="cancel-logo"
                                />
                            </b-button>
                        </div>
                    </b-form>
                </validation-observer>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import { BFormInput, BSidebar, BForm, BFormGroup, BButton, BPopover, BFormTextarea } from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import vSelect from "vue-select";
import formValidation from "@core/comp-functions/forms/form-validation";
import { toRefs, ref, watch } from "@vue/composition-api";
import { debounce } from 'debounce';
import IdType from "@/models/IdType";
import Director from "@/models/Director";
import { NO_MATCHING_OPTIONS } from "@/constants/errors";
import { CHANGE_OF_TYPES, SINGAPORE_CITIZEN } from "@/constants/config";
import { generateRandomUniqueId, getIdType } from "@/helpers";
import { errorToast } from "@/views/helpers/toast";

export default {
    components: {
        // BSV
        BForm,
        BButton,
        BSidebar,
        BFormGroup,
        BFormInput,
        BFormTextarea,
        BPopover,
        vSelect,

        // Form Validation
        ValidationProvider,
        ValidationObserver,
    },

    directives: {
        Ripple,
    },

    model: {
        prop: "isGenerateDocumentSidebarActive",
        event: "update:is-generate-document-sidebar-active",
    },

    props: {
        isGenerateDocumentSidebarActive: {
            type: Boolean,
            required: true,
        },

        dataToView: {
            type: Object,
            required: true,
        },
        
        dataStatus: { 
            type: String,
            default: "view"
        },

        dataSidebarActionLoading: {
            type: Boolean,
            default: false,
        },
    },

    
    data() {
        return {
            required,

            isFetching: false,
            changeOf: null,
            NO_MATCHING_OPTIONS,
            selectedDirectorOriginData: null,
            selectedDirectorData: {},
            directorOptions: [],
            idTypeOptions: [],

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            updatedDirectorParticularList: [],
        };
    },

    computed: {
        isDisabled() {
            return this.dataStatus === "view";
        },

        sideBarTitle() {
            return "Update of Particulars of Director";
        },

        dynamicStyle() {
            if(this.dataStatus === "view")
                return "background: #efefef; cursor: context-menu!important";
            return "background: white;";
        },

        showUpdatedParticularListTable() {
            return this.updatedDirectorParticularList.length > 0;
        }
    },

    watch: {
        isGenerateDocumentSidebarActive(active) {
            if(active) {
                if(this.dataStatus === 'view' ) {
                    this.setupForViewData();
                } else {
                    this.changeOf = null;

                    if(this.directorOptions.length < 1) {
                        this.fetchRequiredData();
                    }

                    // reset local data cache
                    this.updatedDirectorParticularList = [];
                    this.selectedDirectorData = {};
                }
            } 
        }
    },

    methods: {
        async fetchRequiredData() {
            this.isFetching = true;

            const directors = await Director.allIncludingNomineeDirector();

            this.directorOptions = directors.map(director => ({
                id: director.id,
                userId: director.user.id,
                nric: director.user.user_info.NRIC,
                name: director.user.user_info?.legal_name ?? director.user.name,
                email: director.user.email,
                contactNumber: director.user.user_info.contact_number,
                address: director.user.user_info.address,
                alternativeAddress: director.user.user_info.alternative_address,
                idType: director.user.user_info.id_type,
                nationality: director.user.user_info.nationality
            }));

            this.idTypeOptions = await IdType.all();

            this.isFetching = false;
        },

        setupForViewData() {
            this.selectedDirectorData = this.dataToView.selectedDirectorData;
            this.updatedDirectorParticularList = this.dataToView.updatedDirectorParticularList;
        },

        handleOnDirectorChange(director) {
            this.selectedDirectorOriginData = director;
            this.selectedDirectorData = JSON.parse(JSON.stringify(director));
            this.updatedDirectorParticularList = [];
            this.particularsOfDirectorLocal.updatedDirectorParticularList = [];

            // setup director idType
            this.particularsOfDirectorLocal.idType = director.idType;
        },

        handleOnChangeIdType(idType) {            
            let type = null;
            
            if(idType.name === 'NRIC (Citizen)' || idType.name === 'NRIC (PERMANENT RESIDENT)')
                type = 'NRIC';
            else if(idType.name.toUpperCase() === 'FIN')
                type = 'FIN';
            else type = 'PASSPORT';

            this.updatedDirectorParticularList.forEach(particular => {
                if(particular.changeOfType === CHANGE_OF_TYPES.PASSPORT) {
                    // eslint-disable-next-line no-param-reassign
                    particular.label = type;
                }
            });
           
        },

        onUpdateDirectorParticulars: debounce(function(changeOf, changeOfType) {
            // passport number
            if(changeOfType === CHANGE_OF_TYPES.PASSPORT) {
                this.updatePassportParticular(changeOf);
            } 
            // contact number
            else if(changeOfType === CHANGE_OF_TYPES.CONTACT) {
                this.updateContactNumberParticular(changeOf);
            }
            // email address
            else if(changeOfType === CHANGE_OF_TYPES.EMAIL) {
                this.updateEmailParticular(changeOf);
            }
            // name
            else if(changeOfType === CHANGE_OF_TYPES.NAME) {
                this.updateNameParticular(changeOf);
            }
            // address
            else if(changeOfType === CHANGE_OF_TYPES.ADDRESS) {
                this.updateAddressParticular(changeOf);
            }
            // alternative address
            else if(changeOfType === CHANGE_OF_TYPES.ALTERNATIVE_ADDRESS) {
                this.updateAlternativeAddressParticular(changeOf);
            }

            // update the value for composition api
            this.particularsOfDirectorLocal.updatedDirectorParticularList = JSON.parse(JSON.stringify(this.updatedDirectorParticularList));
        }, 300),

        updatePassportParticular(changeOf) {
            if(this.selectedDirectorOriginData.nric.toUpperCase() !== changeOf.toUpperCase()) {

                // id type option set and get label
                const updatedIdType = getIdType(changeOf.toUpperCase());
                const idTypeLabel = this.setupDirectorIdTypeAndGetLabel(updatedIdType);
                
                const existingParticular = this.updatedDirectorParticularList.filter(list => list.changeOfType === CHANGE_OF_TYPES.PASSPORT)[0];
                if(existingParticular) {
                    existingParticular.changeTo = changeOf.toUpperCase();
                    existingParticular.label = idTypeLabel;
                } else {
                    this.updatedDirectorParticularList.push({
                        _id: generateRandomUniqueId(),
                        changeOfType: CHANGE_OF_TYPES.PASSPORT,
                        changeFrom: this.selectedDirectorOriginData.nric.toUpperCase(),
                        changeTo: changeOf.toUpperCase(),
                        label: idTypeLabel,
                    });
                }
                    
                // equal mean doesn't change and should be remove
            } else {
                this.updatedDirectorParticularList = this.updatedDirectorParticularList.filter(list => list.changeOfType !== CHANGE_OF_TYPES.PASSPORT);
                this.particularsOfDirectorLocal.idType = this.selectedDirectorOriginData.idType;
            }
        },

        updateContactNumberParticular(changeOf) {
            if(this.selectedDirectorOriginData.contactNumber.toUpperCase() !== changeOf.toUpperCase()) {
                const existingParticular = this.updatedDirectorParticularList.filter(list => list.changeOfType === CHANGE_OF_TYPES.CONTACT)[0];
                if(existingParticular) {
                    existingParticular.changeTo = changeOf.toUpperCase();
                } else {
                    this.updatedDirectorParticularList.push({
                        _id: generateRandomUniqueId(),
                        changeOfType: CHANGE_OF_TYPES.CONTACT,
                        changeFrom: this.selectedDirectorOriginData.contactNumber.toUpperCase(),
                        changeTo: changeOf.toUpperCase(),
                        label: CHANGE_OF_TYPES.CONTACT,
                    });
                }
                    
                // when equal case should be remove if exist
            } else {
                this.updatedDirectorParticularList = this.updatedDirectorParticularList.filter(list => list.changeOfType !== CHANGE_OF_TYPES.CONTACT);
            }
        },

        updateEmailParticular(changeOf) {
            if(this.selectedDirectorOriginData.email.toUpperCase() !== changeOf.toUpperCase()) {
                const existingParticular = this.updatedDirectorParticularList.filter(list => list.changeOfType === CHANGE_OF_TYPES.EMAIL)[0];
                if(existingParticular) {
                    existingParticular.changeTo = changeOf.toUpperCase();
                } else {
                    this.updatedDirectorParticularList.push({
                        _id: generateRandomUniqueId(),
                        changeOfType: CHANGE_OF_TYPES.EMAIL,
                        changeFrom: this.selectedDirectorOriginData.email.toUpperCase(),
                        changeTo: changeOf.toUpperCase(),
                        label: CHANGE_OF_TYPES.EMAIL,
                    });
                }
                    
                // when equal case should be remove if exist
            } else {
                this.updatedDirectorParticularList = this.updatedDirectorParticularList.filter(list => list.changeOfType !== CHANGE_OF_TYPES.EMAIL);
            }
        },

        updateNameParticular(changeOf) {
            if(this.selectedDirectorOriginData.name.toUpperCase() !== changeOf.toUpperCase()) {
                const existingParticular = this.updatedDirectorParticularList.filter(list => list.changeOfType === CHANGE_OF_TYPES.NAME)[0];
                if(existingParticular) {
                    existingParticular.changeTo = changeOf.toUpperCase();
                } else {
                    this.updatedDirectorParticularList.push({
                        _id: generateRandomUniqueId(),
                        changeOfType: CHANGE_OF_TYPES.NAME,
                        changeFrom: this.selectedDirectorOriginData.name.toUpperCase(),
                        changeTo: changeOf.toUpperCase(),
                        label: CHANGE_OF_TYPES.NAME,
                    });
                }
                    
                // when equal case should be remove if exist
            } else {
                this.updatedDirectorParticularList = this.updatedDirectorParticularList.filter(list => list.changeOfType !== CHANGE_OF_TYPES.NAME);
            }
        },

        updateAddressParticular(changeOf) {
            if(this.selectedDirectorOriginData.address.toUpperCase() !== changeOf.toUpperCase()) {
                const existingParticular = this.updatedDirectorParticularList.filter(list => list.changeOfType === CHANGE_OF_TYPES.ADDRESS)[0];
                if(existingParticular) {
                    existingParticular.changeTo = changeOf.toUpperCase();
                } else {
                    this.updatedDirectorParticularList.push({
                        _id: generateRandomUniqueId(),
                        changeOfType: CHANGE_OF_TYPES.ADDRESS,
                        changeFrom: this.selectedDirectorOriginData.address.toUpperCase(),
                        changeTo: changeOf.toUpperCase(),
                        label: 'Address'
                    });
                }
                    
                // when equal case should be remove if exist
            } else {
                this.updatedDirectorParticularList = this.updatedDirectorParticularList.filter(list => list.changeOfType !== CHANGE_OF_TYPES.ADDRESS);
            }
        },

        updateAlternativeAddressParticular(changeOf) {
            if(changeOf && this.selectedDirectorOriginData.alternativeAddress?.toUpperCase() !== changeOf.toUpperCase()) {
                const existingParticular = this.updatedDirectorParticularList.filter(list => list.changeOfType === CHANGE_OF_TYPES.ALTERNATIVE_ADDRESS)[0];
                if(existingParticular) {
                    existingParticular.changeTo = changeOf.toUpperCase();
                } else {
                    this.updatedDirectorParticularList.push({
                        _id: generateRandomUniqueId(),
                        changeOfType: CHANGE_OF_TYPES.ALTERNATIVE_ADDRESS,
                        changeFrom: this.selectedDirectorOriginData.alternativeAddress?.toUpperCase(),
                        changeTo: changeOf.toUpperCase(),
                        label: 'Alternative Address'
                    });
                }
                    
                // when equal case should be remove if exist
            } else {
                this.updatedDirectorParticularList = this.updatedDirectorParticularList.filter(list => list.changeOfType !== CHANGE_OF_TYPES.ALTERNATIVE_ADDRESS);
            }
        },

        remove(particular) {
            // passport number
            if(particular.changeOfType === CHANGE_OF_TYPES.PASSPORT) {
                this.selectedDirectorData.nric = this.selectedDirectorOriginData.nric;
                this.particularsOfDirectorLocal.idType = this.selectedDirectorOriginData.idType;
            } 
            // contact number
            else if(particular.changeOfType === CHANGE_OF_TYPES.CONTACT) {
                this.selectedDirectorData.contactNumber = this.selectedDirectorOriginData.contactNumber;
            }
            // email address
            else if(particular.changeOfType === CHANGE_OF_TYPES.EMAIL) {
                this.selectedDirectorData.email = this.selectedDirectorOriginData.email;
            }
            // name
            else if(particular.changeOfType === CHANGE_OF_TYPES.NAME) {
                this.selectedDirectorData.name = this.selectedDirectorOriginData.name;
            }
            // address
            else if(particular.changeOfType === CHANGE_OF_TYPES.ADDRESS) {
                this.selectedDirectorData.address = this.selectedDirectorOriginData.address;
            }
            // alternative address
            else if(particular.changeOfType === CHANGE_OF_TYPES.ALTERNATIVE_ADDRESS) {
                this.selectedDirectorData.alternativeAddress = this.selectedDirectorOriginData.alternativeAddress;
            }
            
            this.updatedDirectorParticularList = this.updatedDirectorParticularList.filter(list => list.changeOfType !== particular.changeOfType);
        },

        setupDirectorIdTypeAndGetLabel(type) {
            let idTypeLabel = type;

            if(type === 'Passport') {
                // eslint-disable-next-line no-param-reassign
                type = 'PASSPORT/OTHERS';
                idTypeLabel = 'PASSPORT';
            }
            if(type === 'NRIC') {
                if(this.selectedDirectorData.nationality.name.toUpperCase() === SINGAPORE_CITIZEN) {
                    // eslint-disable-next-line no-param-reassign
                    type = 'NRIC (Citizen)';
                } else {
                    // eslint-disable-next-line no-param-reassign
                    type = 'NRIC (PERMANENT RESIDENT)';
                }

                idTypeLabel = 'NRIC';
            }

            const idType = this.idTypeOptions.filter(
                opt => opt.name.toUpperCase() === type.toUpperCase()
            )[0];

            this.particularsOfDirectorLocal.idType = idType;
            return idTypeLabel;
        }
    },

    setup(props, { emit }) {
        // eslint-disable-next-line no-shadow
        function useParticularsOfDirectorHandler(props, emit) {
            const particularsOfDirectorLocal = ref(JSON.parse(JSON.stringify(props.dataToView.value)));

            const resetParticularOfDirectorLocal = () => {
                // eslint-disable-next-line no-use-before-define
                clearForm();
                particularsOfDirectorLocal.value = JSON.parse(JSON.stringify(props.dataToView.value));
            };

            watch(props.dataToView, () => {
                resetParticularOfDirectorLocal();
            });
           
            const onSubmit = () => {
                // when nothing particular update
                if(particularsOfDirectorLocal.value.updatedDirectorParticularList.length < 1) 
                {
                    errorToast('Require to update at least one particular to proceed.');
                    return;
                }

                const data = JSON.parse(JSON.stringify(particularsOfDirectorLocal));

                emit('generate', data.value);
            };

            return {
                particularsOfDirectorLocal, 

                // UI
                onSubmit,
                resetParticularOfDirectorLocal,
            };
        };
         
        const {
            particularsOfDirectorLocal,

            // UI
            onSubmit, 
            resetParticularOfDirectorLocal
        } = useParticularsOfDirectorHandler(toRefs(props), emit);

        const { refFormObserver, getValidationState, resetForm, clearForm } = formValidation(particularsOfDirectorLocal);
        
        function clearFormData(){
            resetParticularOfDirectorLocal();
            clearForm();
        };

        return {
            // Add New
            onSubmit,
            particularsOfDirectorLocal,

            // Form Validation
            resetForm,
            clearForm,
            refFormObserver,
            getValidationState, 
            
            // Methods
            clearFormData,
        };
    },
};
</script>

<style scope>
.particulars-of-director-create-or-view .b-sidebar.sidebar-lg {
    width: 31rem;
}

.particulars-of-director-create-or-view .preloader {
    width: 15px;
    height: 15px;
    left: 29% !important;
}

.particulars-of-director-create-or-view .preloader div:before {
    background: var(--primary-color);
}

.particulars-of-director-create-or-view .action-button .preloader div:before {
    background: white;
}

.side-bar-header{
   background: #f6f6f6 !important;
 } 
 
/* select disabled color overwrite */
.vs--disabled .vs__dropdown-toggle {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__search {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__open-indicator {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__selected{
    cursor: context-menu!important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>